import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';

import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { CatalogedCompanySettingKey } from '~pages/Settings/Configuration/companySettingsCatalog';
import { useStores } from '~store';
import UserStore from '~store/UserStore';

import {
  companySettingCategoryLabelKeys,
  companySettingKeys,
  configurationPageCategories,
  settingKeysInCategory,
} from './companySettingsCatalog';
import AllowedUnitsSetting from './components/AllowedUnitsSetting';
import AutoOrderCompletionSetting from './components/AutoOrderCompletionSetting';
import CompanySettingToggle from './components/CompanySettingToggle';
import DateFormatSetting from './components/DateFormatSetting';
import DefaultPaymentTermSetting from './components/DefaultPaymentTermSetting';
import DefaultProjectTermsSetting from './components/DefaultProjectTermsSetting';
import DisplaySettings from './components/DisplaySettings';
import GeofenceCreationSetting from './components/GeofenceCreationSetting';
import GeofenceTimerSetting from './components/GeofenceTimerSetting';
import OnsiteDefaultsSetting from './components/OnsiteDefaultsSetting';
import useVisibleCompanySettingKeys from './useVisibleCompanySettingKeys';

type SettingDefinition = {
  element: {
    component: () => JSX.Element | null;
    canAccess?: (
      userStore: UserStore,
      visibleSettingKeys: CatalogedCompanySettingKey[],
    ) => boolean;
  };
  label?: string;
};

export const companySettingsSections: SettingDefinition[] =
  configurationPageCategories.map((category) => ({
    element: {
      component: function CompanySettingsCategorySection() {
        const visibleSettingKeys = useVisibleCompanySettingKeys();
        const categorySettingKeys = settingKeysInCategory(visibleSettingKeys, category);

        if (category === 'display') {
          return <DisplaySettings settingKeys={categorySettingKeys} />;
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {categorySettingKeys.map((key) => (
              <CompanySettingToggle key={key} settingKey={key} />
            ))}
          </Box>
        );
      },
      canAccess: (
        userStore: UserStore,
        visibleSettingKeys: CatalogedCompanySettingKey[],
      ) => {
        if (!userStore.getPermissions().canEditCompany) return false;
        // The BE rejects settings writes for the Tread super-company (403). Hide the
        // section to avoid a dead-end UI, mirroring DefaultProjectTermsSetting.
        if (userStore.userCompany.id === TREAD_UUID) return false;

        return settingKeysInCategory(visibleSettingKeys, category).length > 0;
      },
    },
    label: t(companySettingCategoryLabelKeys[category]),
  }));

const settings: SettingDefinition[] = [
  {
    element: AutoOrderCompletionSetting,
    label: t('administration.configuration.settings.auto_order_completion.setting_title'),
  },
  {
    element: GeofenceCreationSetting,
    label: t('administration.configuration.settings.geofence_creation.setting_title'),
  },
  {
    element: GeofenceTimerSetting,
    label: t('administration.configuration.settings.geofence_timer.setting_title'),
  },
  {
    element: DateFormatSetting,
    label: t('administration.configuration.settings.date_format.setting_title'),
  },
  {
    element: OnsiteDefaultsSetting,
    label: t('administration.configuration.settings.onsite_defaults.setting_title'),
  },
  {
    element: AllowedUnitsSetting,
    label: t('administration.configuration.settings.allowed_units.setting_title'),
  },
  {
    element: DefaultProjectTermsSetting,
    label: t('administration.configuration.settings.default_project_terms.setting_title'),
  },
  {
    element: DefaultPaymentTermSetting,
    label: t('administration.configuration.settings.default_payment_term.setting_title'),
  },
  ...companySettingsSections,
];

export const userHasAccessToConfigurationManagement = (userStore: UserStore) => {
  return settings.some((item) => {
    return item.element.canAccess?.(userStore, companySettingKeys) ?? true;
  });
};

export default function ConfigurationManagement() {
  const { userStore } = useStores();
  const visibleCompanySettingKeys = useVisibleCompanySettingKeys();

  return (
    <Box
      sx={{
        maxWidth: '800px',
      }}
    >
      <SettingsPageHeader
        title={t('administration.configuration.title')}
        description={t('settings.page_descriptions.configuration')}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {settings.map((setting) => {
          const hasAccess =
            setting.element.canAccess?.(userStore, visibleCompanySettingKeys) ?? true;

          if (!hasAccess) return null;

          if (!setting.label) {
            return <setting.element.component key={setting.element.component.name} />;
          }

          return (
            <Box key={setting.label}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'normal',
                }}
              >
                {setting.label}
              </Typography>
              <setting.element.component />
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
